import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { FloatingHeader } from "./floating-header";

// -------------------------------------------------------------------
// Section data (GitBook-style: icons on sections only)
// -------------------------------------------------------------------
type DocSection = {
  title: string;
  items: { label: string; href: string }[];
};

const DOC_SECTIONS: DocSection[] = [
  {
    title: "Getting Started",
    items: [
      { label: "Welcome to Nordex", href: "/welcome" },
      { label: "Connecting Your Wallet", href: "/connect-wallet" },
      { label: "Making Your First Deposit", href: "/first-deposit" },
      { label: "Deposits & Withdrawals", href: "/withdrawals" },
      { label: "Placing Your First Trade", href: "/first-trade" },
      { label: "Understanding the Interface", href: "/interface" },
    ],
  },
  {
    title: "Trading Guides",
    items: [
      { label: "Understanding Perpetual Futures", href: "/perpetuals" },
      { label: "Leverage Trading Explained", href: "/leverage" },
      { label: "Margin & Liquidation", href: "/margin-liquidation" },
      { label: "Funding Rates", href: "/funding-rates" },
      { label: "Order Types", href: "/order-types" },
      { label: "Position Management", href: "/position-management" },
    ],
  },
  {
    title: "Markets & Fees",
    items: [
      { label: "Available Markets", href: "/markets" },
      { label: "Fee Structure", href: "/fees" },
    ],
  },
  {
    title: "Platform Features",
    items: [
      { label: "Account Overview", href: "/account" },
      { label: "Charts & Analysis Tools", href: "/charts" },
      { label: "Performance Lab", href: "/performance-lab" },
    ],
  },
  {
    title: "Security & Best Practices",
    items: [
      { label: "Wallet Security", href: "/wallet-security" },
      { label: "Understanding Risks", href: "/understanding-risks" },
    ],
  },
];

// -------------------------------------------------------------------
// Collapsible section state helpers
// -------------------------------------------------------------------
const COLLAPSE_KEY = "nordex-docs-collapse";

function getCollapsedSections(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(COLLAPSE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveCollapsedSections(state: Record<string, boolean>) {
  localStorage.setItem(COLLAPSE_KEY, JSON.stringify(state));
}

// -------------------------------------------------------------------
// Sidebar Navigation
// -------------------------------------------------------------------
function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const stored = getCollapsedSections();
    // Auto-expand section containing current page
    const result: Record<string, boolean> = {};
    DOC_SECTIONS.forEach((section) => {
      const isActive = section.items.some(
        (item) => item.href === location.pathname
      );
      result[section.title] = isActive
        ? false
        : (stored[section.title] ?? false);
    });
    return result;
  });

  const toggleSection = (title: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [title]: !prev[title] };
      saveCollapsedSections(next);
      return next;
    });
  };

  // Auto-expand when navigating to a page in a collapsed section
  useEffect(() => {
    setCollapsed((prev) => {
      const next = { ...prev };
      let changed = false;
      DOC_SECTIONS.forEach((section) => {
        const isActive = section.items.some(
          (item) => item.href === location.pathname
        );
        if (isActive && prev[section.title]) {
          next[section.title] = false;
          changed = true;
        }
      });
      if (changed) saveCollapsedSections(next);
      return changed ? next : prev;
    });
  }, [location.pathname]);

  return (
    <nav className="py-6 px-3 space-y-1">
      {DOC_SECTIONS.map((section) => {
        const isCollapsed = collapsed[section.title] ?? false;
        const sectionIsActive = section.items.some(
          (item) => item.href === location.pathname
        );

        return (
          <div key={section.title} className="mb-1">
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.title)}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer",
                sectionIsActive
                  ? "text-[var(--sidebar-text-active)]"
                  : "text-[var(--sidebar-section)] hover:text-[var(--sidebar-text-active)]"
              )}
            >
              <span className="flex-1 text-left">{section.title}</span>
              <ChevronDown
                className={cn(
                  "h-3 w-3 transition-transform duration-200",
                  isCollapsed && "-rotate-90"
                )}
              />
            </button>

            {/* Section Items (collapsible) */}
            <div
              className="sidebar-section-items"
              data-collapsed={isCollapsed}
            >
              <div>
                <ul className="mt-0.5 ml-2 border-l border-[var(--sidebar-border)]">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={onNavigate}
                          className={cn(
                            "block pl-4 pr-2 py-1.5 text-[13px] leading-snug border-l -ml-px transition-colors",
                            isActive
                              ? "border-foreground text-[var(--sidebar-text-active)] font-medium bg-[var(--sidebar-active)]"
                              : "border-transparent text-[var(--sidebar-text)] hover:text-[var(--sidebar-text-active)] hover:bg-[var(--sidebar-hover)]"
                          )}
                        >
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

// -------------------------------------------------------------------
// Main Layout
// -------------------------------------------------------------------
type DocsLayoutProps = {
  children: React.ReactNode;
  fullWidth?: boolean;
};

export function DocsLayout({ children, fullWidth }: DocsLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div className="min-h-screen bg-dot-pattern">
      {/* Floating Pill Menu */}
      <FloatingHeader />

      {/* Spacer for fixed header */}
      <div className="h-[85px]" />

      {/* Centered two-card container */}
      <div className="max-w-[1340px] mx-auto px-2 sm:px-4 lg:px-6 pb-6">
        <div className="flex gap-3 items-start min-h-[calc(100vh-110px)]">
          {/* Desktop Sidebar Card */}
          {!isMobile && (
            <aside className="docs-card w-64 shrink-0 rounded-xl border border-border sticky top-[89px] h-[calc(100vh-110px)] overflow-y-auto">
              <SidebarNav />
            </aside>
          )}

          {/* Mobile Sidebar Overlay */}
          {isMobile && sidebarOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm"
                onClick={() => setSidebarOpen(false)}
              />
              <aside className="fixed inset-y-0 left-0 z-50 w-72 docs-card border-r border-border shadow-2xl overflow-y-auto rounded-r-xl">
                <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-border">
                  <span className="text-sm font-semibold">Documentation</span>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-1.5 rounded-md hover:bg-[var(--sidebar-hover)] transition-colors cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <SidebarNav onNavigate={() => setSidebarOpen(false)} />
              </aside>
            </>
          )}

          {/* Main Content Card */}
          <main className="docs-card flex-1 min-w-0 rounded-xl border border-border">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="fixed bottom-6 right-6 z-30 p-3 rounded-full bg-foreground text-background shadow-lg hover:scale-105 transition-transform cursor-pointer"
                aria-label="Open documentation menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}

            <div className="py-10 px-4 md:px-10 lg:px-14">
              <div className={fullWidth ? "docs-content" : "max-w-3xl docs-content"}>{children}</div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export { DOC_SECTIONS };
